import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateActionsPageSelectedRastersDictionary } from 'redux/actions';
import { reactTableSortOnClickInFuncComp } from 'common/utils/helpers';
import { polarizationTranslator } from 'components/features/HeatmapViewAndExportModal/utils/helpers';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import moment from 'moment-timezone';
import ScrollReactTable from 'components/shared/ScrollReactTable/ScrollReactTable';
import ActionTwoHopProgressTableCheckboxCell from './ActionTwoHopProgressTableIdCell/ActionTwoHopProgressTableCheckboxCell';
import ActionTwoHopProgressTableRow from './ActionTwoHopProgressTableRow/ActionTwoHopProgressTableRow';
import DeviceTableCell from 'components/features/DeviceView/DeviceTable/DeviceTableCells/DeviceTableCell';
import ActionTwoHopProgressTableOverlay from './ActionTwoHopProgressTableOverlay/ActionTwoHopProgressTableOverlay';
import ActionTwoHopProgressTableProgressCell from './ActionTwoHopProgressTableProgressCell/ActionTwoHopProgressTableProgressCell';
import ActionTwoHopProgressTableScoreCell from './ActionTwoHopProgressTableScoreCell/ActionTwoHopProgressTableScoreCell';
import './ActionTwoHopProgressTable.css';

const ActionTwoHopProgressTable = (props) => {
  const dispatch = useDispatch();
  const [sorted, setSorted] = useState([]);
  const twoHopStatusInstanceDictionary = useSelector(
    (store) => store.ui.actionsPage?.twoHopStatusInstanceDictionary?.[props.alignmentId] ?? {}
  );
  const rowIdx = useRef(0);

  useEffect(() => {
    if (
      twoHopStatusInstanceDictionary &&
      Object.keys(twoHopStatusInstanceDictionary)?.length > 0 &&
      !props.isGetTwoHopAlignmentCalled
    ) {
      let twoHopSelectedRastersDictionary = {};
      let rowKeys = Object.keys(twoHopStatusInstanceDictionary);
      rowKeys.forEach((rowKey) => {
        twoHopSelectedRastersDictionary[rowKey] = false;
      });
      dispatch(updateActionsPageSelectedRastersDictionary(props.alignmentId, twoHopSelectedRastersDictionary));
    }
  }, [twoHopStatusInstanceDictionary, props.alignmentId]);

  const columns = [
    {
      id: 'date',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv" data-tip={'Date'}>
            <IconAndText
              img={null}
              hoverImg={null}
              text={'DATE'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'date')}
            />
            <div
              id="ActionTwoHopProgressTable_ResizerSliderIcon"
              className="ActionTwoHopProgressTable_ResizerSliderIcon"
            >
              <div
                id="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
                className="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      accessor: (record) => {
        return record?.Timestamp;
      },
      Cell: (row) => {
        let displayString = moment(row.value)?.format('MMM D YYYY');
        return <DeviceTableCell displayString={displayString} />;
      }
    },
    {
      id: 'time',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv" data-tip={'Time'}>
            <IconAndText
              img={null}
              hoverImg={null}
              text={'TIME'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'time')}
            />
            <div
              id="ActionTwoHopProgressTable_ResizerSliderIcon"
              className="ActionTwoHopProgressTable_ResizerSliderIcon"
            >
              <div
                id="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
                className="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      accessor: (record) => {
        return record?.Timestamp;
      },
      Cell: (row) => {
        let displayString = moment(row.value)?.format('HH:mm:ss');
        return <DeviceTableCell displayString={displayString} />;
      }
    },
    {
      id: 'polarization_type',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv" data-tip={'Polarization'}>
            <IconAndText
              img={null}
              hoverImg={null}
              text={'POLARIZATION'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'polarization_type')}
            />
            <div
              id="ActionTwoHopProgressTable_ResizerSliderIcon"
              className="ActionTwoHopProgressTable_ResizerSliderIcon"
            >
              <div
                id="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
                className="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      accessor: (record) => {
        let polarization = polarizationTranslator(record?.polarization);

        return polarization;
      },
      Cell: (row) => {
        return <DeviceTableCell displayString={row.value} />;
      }
    },
    {
      id: 'dither_az',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv" data-tip={'Dither azimuth'}>
            <IconAndText
              img={null}
              hoverImg={null}
              text={'Azimuth'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'dither_az')}
            />
            <div
              id="ActionTwoHopProgressTable_ResizerSliderIcon"
              className="ActionTwoHopProgressTable_ResizerSliderIcon"
            >
              <div
                id="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
                className="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      accessor: (record) => {
        let ditherAz = record?.az ?? '00';
        return ditherAz;
      },
      Cell: (row) => {
        return <DeviceTableCell center={true} displayString={row.value} />;
      }
    },
    {
      id: 'dither_el',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv" data-tip={'Dither elevation'}>
            <IconAndText
              img={null}
              hoverImg={null}
              text={'Elevation'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'dither_el')}
            />
            <div
              id="ActionTwoHopProgressTable_ResizerSliderIcon"
              className="ActionTwoHopProgressTable_ResizerSliderIcon"
            >
              <div
                id="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
                className="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      accessor: (record) => {
        let ditherEl = record?.el ?? '00';
        return ditherEl;
      },
      Cell: (row) => {
        return <DeviceTableCell center={true} displayString={row.value} />;
      }
    },
    {
      id: 'score',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        return (
          <div
            id="DeviceTable_Header_OuterDiv"
            className="DeviceTable_Header_OuterDiv"
            data-tip={'The average of the top 15 heatmap values'}
          >
            <IconAndText
              img={null}
              hoverImg={null}
              text={'SCORE'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'score')}
            />
            <div
              id="ActionTwoHopProgressTable_ResizerSliderIcon"
              className="ActionTwoHopProgressTable_ResizerSliderIcon"
            >
              <div
                id="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
                className="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      accessor: (record) => {
        let score = record?.score;
        return score;
      },
      Cell: (row) => {
        const original = row.original;
        const alignmentId = original?.PartitionKey;
        const rowKey = original?.RowKey
        return <ActionTwoHopProgressTableScoreCell alignmentId={alignmentId} rowKey={rowKey} />
      }
    },
    {
      id: 'progress',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv" data-tip={'Progress'}>
            <IconAndText
              img={null}
              hoverImg={null}
              text={'PROGRESS'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'progress')}
            />
            <div
              id="ActionTwoHopProgressTable_ResizerSliderIcon"
              className="ActionTwoHopProgressTable_ResizerSliderIcon"
            >
              <div
                id="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
                className="ActionTwoHopProgressTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      accessor: (record) => {
        const progress = record?.progress
        return progress;
      },
      Cell: (row) => {
        const original = row.original;
        const alignmentId = original?.PartitionKey;
        const rowKey = original?.RowKey
        return (
          <ActionTwoHopProgressTableProgressCell alignmentId={alignmentId} rowKey={rowKey} />
        );
      }
    },
    {
      id: 'view_checkbox',
      minWidth: 60,
      resizable: false,
      sortable: false,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv" data-tip={'View (Up to two)'}>
            <IconAndText
              img={null}
              hoverImg={null}
              text={'VIEW(UP TO TWO)'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'view_checkbox')}
            />
          </div>
        );
      },
      accessor: (record) => {
        // let rasterId = record?.rasterId;
        let donorDevice = props.donorDevice;
        let donorDevice2 = props.donorDevice2;
        let alignmentId = record?.PartitionKey;
        let rowKey = record?.RowKey;
        // let progress = record?.progress;
        return { donorDevice, alignmentId, rowKey, donorDevice2 };
      },
      Cell: (row) => {
        return (
          <ActionTwoHopProgressTableCheckboxCell
            // rasterId={row.value?.rasterId}
            sourceText={''}
            // deviceHeatmapHistoryDictionary={deviceHeatmapHistoryDictionary}
            alignmentId={row.value?.alignmentId}
            rowKey={row.value?.rowKey}
            // twoHopStatusInstanceDictionary={twoHopStatusInstanceDictionary}
            donorDevice={row.value?.donorDevice}
            donorDevice2={row.value?.donorDevice2}
            // progress={row.value?.progress}
          />
        );
      }
    }
  ];

  const handleOnSortedChange = (newSorted) => {
    setSorted(newSorted);
  };

  let pageSize = 7;
  let numOfInstances = 0;
  if (twoHopStatusInstanceDictionary) {
    pageSize = Object.keys(twoHopStatusInstanceDictionary)?.length;
    numOfInstances = Object.keys(twoHopStatusInstanceDictionary)?.length;
  }
  let overlay =
    twoHopStatusInstanceDictionary && Object.keys(twoHopStatusInstanceDictionary)?.length === 0 ? (
      <ActionTwoHopProgressTableOverlay
        isGetTwoHopAlignmentCalled={props.isGetTwoHopAlignmentCalled}
        alignmentId={props.alignmentId}
        twoHopStatusInstanceDictionary={twoHopStatusInstanceDictionary}
      />
    ) : null;

  return (
    <div id="ActionTwoHopProgressTable_Container" className="ActionTwoHopProgressTable_Container">
      <div
        id="ActionTwoHopProgressViewModal_RasterCount_Header"
        className="ActionTwoHopProgressViewModal_RasterCount_Header"
      >
        Number of Rasters: <span style={{ fontWeight: 'bold' }}>{numOfInstances}</span>
      </div>
      <div id="ActionTwoHopProgressTable_Div" className="ActionTwoHopProgressTable_Div">
        <ScrollReactTable
          showPagination={false}
          noDataText={''}
          className={'-highlight Global_Table ActionTwoHopProgressTable'}
          resizable={true}
          sortable={false}
          pageSize={pageSize < 9 ? 9 : pageSize}
          data={twoHopStatusInstanceDictionary ? Object.values(twoHopStatusInstanceDictionary) : []}
          overlay={overlay}
          fetching={false}
          sorted={sorted}
          onSortedChange={(newSorted) => handleOnSortedChange(newSorted)}
          columns={columns}
          getTdProps={(state, rowInfo, column, instance) => {
            return {
              onClick: (e, handleOriginal) => {
                // e.preventDefault();
                if (rowInfo && rowInfo.index !== null) {
                  let record = rowInfo.original;
                  let rasterId = record.rasterId;
                  // let deviceId = record.deviceId;
                  rowIdx.current = rowInfo.index;
                  // dispatch(updateSingleDevicePageSelectedRasterId(rasterId));
                }
                if (handleOriginal) {
                  handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                }
              }
            };
          }}
          getTrProps={(state, rowInfo) => {
            if (rowInfo) {
              let isMatchedIdx = rowInfo.index === rowIdx.current;
              return {
                style: {
                  background: isMatchedIdx ? '#f5f8fa' : 'white',
                  border: isMatchedIdx ? 'solid 1px #ffa471' : 'solid 1px #CBCBCB',
                  borderRadius: '2px'
                }
              };
            }
            return {};
          }}
          TrComponent={(props, index) => <ActionTwoHopProgressTableRow {...props} />}
          // TheadComponent={(props) => (
          //   <ActionTwoHopProgressTableHeader
          //     {...props}
          //     hasCheckbox={props.hasCheckbox}
          //   />
          // )}
        />
      </div>
    </div>
  );
};

export default React.memo(ActionTwoHopProgressTable);
